import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { GripVertical, Plus, RotateCcw, X } from 'lucide-react';
import {
  DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { NoteTemplate, NoteTemplateItem, NoteTemplatePresetKey, Subject } from '@repo/shared-types';
import { cn } from '@repo/ui/src/lib/utils';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { ResponsiveSheet, useResponsiveSheetClose } from '../../components/ResponsiveSheet';
import { useSaveNoteTemplate } from '../../hooks/useSubjects';
import { isPristinePreset, presetWithFreshIds, freshItemId } from '../../lib/noteTemplatePresets';

type GroupKey = 'classItems' | 'studentItems' | 'homeworkItems';
const GROUPS: GroupKey[] = ['classItems', 'studentItems', 'homeworkItems'];

/** Rows the editor shows: the saved item plus a stable client key so a row that
 *  was just added (id assigned client-side, name still empty) keeps its inputs. */
type Draft = NoteTemplate;

const countItems = (t: Draft) => GROUPS.reduce((n, g) => n + t[g].filter((i) => i.name.trim()).length, 0);
const stripNameless = (t: Draft): NoteTemplate => ({
  presetKey: t.presetKey,
  classItems: t['classItems'].filter((i) => i.name.trim()).map(trimItem),
  studentItems: t.studentItems.filter((i) => i.name.trim()).map(trimItem),
  homeworkItems: t.homeworkItems.filter((i) => i.name.trim()).map(trimItem),
});
const trimItem = (i: NoteTemplateItem): NoteTemplateItem => ({ id: i.id, name: i.name.trim(), hint: i.hint?.trim() ? i.hint.trim() : null });

/**
 * E1 editor (handoff 19 §4, port guide 20 §2): three group cards, one sortable
 * row per item (grip · name · hint · X), "Thêm mục" per group, footer with
 * "Về preset" (desktop, non-custom only) · Huỷ · "Lưu sườn". Draft state lives
 * HERE (the ResponsiveSheet contract: children are controlled), keyed off
 * `initial` each time the sheet opens.
 */
export function NoteTemplateEditor({ subject, open, initial, onOpenChange }: {
  subject: Subject;
  open: boolean;
  /** Prefilled from the saved template or a fresh preset; never saved until "Lưu sườn". */
  initial: NoteTemplate;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('progress');
  const isMobile = useIsMobile();
  const save = useSaveNoteTemplate(subject.id);
  const [draft, setDraft] = useState<Draft>(initial);
  const [baseline, setBaseline] = useState<string>(JSON.stringify(initial));
  const [resetOpen, setResetOpen] = useState(false);
  // Which freshly added row should grab focus; state, not a ref, because it
  // is read during render (react-hooks/refs).
  const [focusId, setFocusId] = useState<string | null>(null);

  // Re-seed when the section opens the editor with a new starting point (a
  // preset pick, or the saved template after a refetch). `openWith` always
  // hands over a fresh object, so identity is the signal; adjusting state
  // during render is React's own pattern for this, not an effect.
  const [seed, setSeed] = useState<NoteTemplate>(initial);
  if (seed !== initial) {
    setSeed(initial);
    setDraft(initial);
    setBaseline(JSON.stringify(initial));
  }

  const dirty = JSON.stringify(draft) !== baseline;
  const presetName = draft.presetKey && draft.presetKey !== 'custom' ? t(`template.preset.${draft.presetKey}`) : null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const update = (g: GroupKey, id: string, patch: Partial<NoteTemplateItem>) =>
    setDraft((d) => ({ ...d, [g]: d[g].map((i) => (i.id === id ? { ...i, ...patch } : i)) }));
  const remove = (g: GroupKey, id: string) => setDraft((d) => ({ ...d, [g]: d[g].filter((i) => i.id !== id) }));
  const add = (g: GroupKey) => {
    const id = freshItemId();
    setFocusId(id);
    setDraft((d) => ({ ...d, [g]: [...d[g], { id, name: '', hint: null }] }));
  };
  const reorder = (g: GroupKey) => (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setDraft((d) => {
      const from = d[g].findIndex((i) => i.id === active.id);
      const to = d[g].findIndex((i) => i.id === over.id);
      return from < 0 || to < 0 ? d : { ...d, [g]: arrayMove(d[g], from, to) };
    });
  };

  const submit = () => {
    const cleaned = stripNameless(draft);
    save.mutate(cleaned, {
      onSuccess: () => {
        setBaseline(JSON.stringify(cleaned));
        setDraft(cleaned);
        toast.success(t('template.savedToast', { subject: subject.name }));
        onOpenChange(false);
      },
    });
  };

  // Reset keeps the id of every row the preset does not actually change
  // (matched by name, then by position) so notes written against those rows
  // stay live; only rows the preset adds get a fresh id (review I9).
  const resetToPreset = () => {
    if (!draft.presetKey || draft.presetKey === 'custom') return;
    const fresh = presetWithFreshIds(draft.presetKey as NoteTemplatePresetKey);
    const keepIds = (cur: NoteTemplateItem[], next: NoteTemplateItem[]): NoteTemplateItem[] => {
      const byName = new Map(cur.map((i) => [i.name.trim().toLowerCase(), i.id]));
      const used = new Set<string>();
      return next.map((it, idx) => {
        let id = byName.get(it.name.trim().toLowerCase());
        if (!id || used.has(id)) id = cur[idx] && !used.has(cur[idx].id) ? cur[idx].id : it.id;
        used.add(id);
        return { ...it, id };
      });
    };
    setDraft({
      ...fresh,
      classItems: keepIds(draft.classItems, fresh.classItems),
      studentItems: keepIds(draft.studentItems, fresh.studentItems),
      homeworkItems: keepIds(draft.homeworkItems, fresh.homeworkItems),
    });
    setResetOpen(false);
  };

  const description = presetName && baseline === JSON.stringify(initial) && !dirty && isPristinePreset(draft)
    ? t('template.editor.descPreset', { preset: presetName })
    : t('template.editor.desc');

  return (
    <ResponsiveSheet
      open={open}
      onOpenChange={onOpenChange}
      a11yTitle={t('template.editor.title', { subject: subject.name })}
      title={t('template.editor.title', { subject: subject.name })}
      description={description}
      dirty={dirty}
      discard={{
        title: t('notes.discardTitle'), body: t('notes.discardBody'),
        confirm: t('notes.discardConfirm'), cancel: t('notes.discardCancel'),
      }}
      footer={
        <EditorFooter
          isMobile={isMobile}
          count={countItems(draft)}
          presetName={presetName}
          saving={save.isPending}
          onReset={() => setResetOpen(true)}
          onSave={submit}
        />
      }
    >
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-3">
        <div className="flex flex-col gap-4">
          {GROUPS.map((g) => (
            <div key={g} className="rounded-lg border border-surface-200 bg-surface-0">
              <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-surface-400">
                {t(`template.group.${g === 'classItems' ? 'class' : g === 'studentItems' ? 'student' : 'homework'}`)}
              </p>
              {draft[g].length === 0 ? (
                <p className="px-4 pb-2 pt-1 text-[12.5px] text-surface-400">{t('template.group.emptyRow')}</p>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={reorder(g)}>
                  <SortableContext items={draft[g].map((i) => i.id)} strategy={verticalListSortingStrategy}>
                    <div className="px-2">
                      {draft[g].map((item) => (
                        <ItemRow
                          key={item.id}
                          item={item}
                          autoFocus={focusId === item.id}
                          onFocused={() => setFocusId((cur) => (cur === item.id ? null : cur))}
                          onChange={(patch) => update(g, item.id, patch)}
                          onRemove={() => remove(g, item.id)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              <button
                type="button"
                onClick={() => add(g)}
                className="inline-flex min-h-[44px] items-center gap-1.5 px-4 text-[12.5px] font-medium text-brand-600 sm:min-h-[36px]"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                {t('template.item.add')}
              </button>
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('template.resetTitle', { preset: presetName ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('template.resetBody')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('template.resetCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={resetToPreset}>{t('template.resetConfirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ResponsiveSheet>
  );
}

function EditorFooter({ isMobile, count, presetName, saving, onReset, onSave }: {
  isMobile: boolean; count: number; presetName: string | null; saving: boolean;
  onReset: () => void; onSave: () => void;
}) {
  const { t } = useTranslation('progress');
  const { requestClose } = useResponsiveSheetClose();
  return (
    <div className="flex items-center gap-2 border-t border-surface-200 bg-surface-0 px-5 pb-4 pt-3">
      {isMobile ? (
        <span className="text-[12.5px] text-surface-500">{t('template.count', { count })}</span>
      ) : presetName ? (
        <button type="button" onClick={onReset} className="inline-flex h-9 items-center gap-1.5 px-2.5 text-[12.5px] font-medium text-surface-600 hover:text-surface-800">
          <RotateCcw className="h-[13px] w-[13px]" strokeWidth={2} />
          {t('template.resetToPreset', { preset: presetName })}
        </button>
      ) : null}
      <Button type="button" variant="ghost" className="ml-auto h-[44px] px-3 sm:h-9" onClick={requestClose}>
        {t('notes.cancel')}
      </Button>
      <Button type="button" className="h-[44px] px-[19.2px] sm:h-9" onClick={onSave} disabled={saving}>
        {t('template.save')}
      </Button>
    </div>
  );
}

function ItemRow({ item, autoFocus, onFocused, onChange, onRemove }: {
  item: NoteTemplateItem;
  autoFocus: boolean;
  onFocused: () => void;
  onChange: (patch: Partial<NoteTemplateItem>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('progress');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const nameRef = useRef<HTMLInputElement>(null);
  // Focus the just-added row on the next frame: an immediate focus inside the
  // vaul Drawer drags the drawer (port guide 18 §2.3).
  useEffect(() => {
    if (!autoFocus) return;
    const id = requestAnimationFrame(() => { nameRef.current?.focus(); onFocused(); });
    return () => cancelAnimationFrame(id);
  }, [autoFocus, onFocused]);
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'grid grid-cols-[20px_1fr_28px] items-start gap-2 border-b border-surface-100 px-2 py-2.5 last:border-b-0',
        isDragging && 'relative z-10 bg-surface-0 shadow-sm',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex h-[36px] cursor-grab touch-none items-center justify-center text-surface-300 max-sm:h-[44px]"
        aria-label={t('template.item.drag')}
      >
        <GripVertical className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
      <div className="flex flex-col gap-1.5">
        <Input
          ref={nameRef}
          value={item.name}
          maxLength={40}
          placeholder={t('template.item.namePh')}
          onChange={(e) => onChange({ name: e.target.value })}
          className="h-[36px] rounded-sm text-sm font-medium max-sm:h-[44px] max-sm:text-[15px]"
        />
        <Input
          value={item.hint ?? ''}
          maxLength={80}
          placeholder={t('template.item.hintPh')}
          onChange={(e) => onChange({ hint: e.target.value === '' ? null : e.target.value })}
          className="h-[32px] rounded-sm border-transparent bg-surface-100 text-[13px] text-surface-600 shadow-none max-sm:h-[40px] max-sm:text-[15px]"
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="flex h-[36px] w-7 items-center justify-center text-surface-400 hover:text-surface-700 max-sm:h-[44px]"
        aria-label={t('template.item.remove')}
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}
